import React, { useMemo, useState } from "react";
import Chart from "components/common/Chart";
import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import CircularProgress from "@mui/material/CircularProgress";
import { usePhcTheme } from "../PhcThemeContext";

const LOSER_COLOR = "#FF1744";
const GAINER_COLOR = "#00E676";
const MOBILE_LOSER = "#FF3B30";
const MOBILE_GAINER = "#34C759";
const MAX_ROWS = 5;

const formatPct = (value, withSign = true) => {
  const num = Math.abs(Number(value) || 0);
  const formatted = num.toFixed(1);
  if (!withSign) return `${formatted}%`;
  const sign = Number(value) < 0 ? "-" : "+";
  return `${sign}${formatted}%`;
};

/** Mobile performer insight card —  */
const MobileInsightCard = ({ label, name, impact, tone, theme }) => {
  const isPositive = tone === "positive";
  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "16px",
        border: theme.mobileCardBorder,
        bgcolor: theme.mobileCardBg,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        p: "17px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Stack spacing="4px" alignItems="flex-start">
        <Text
          sx={{
            fontSize: 12,
            color: theme.mobileLabelColor,
            letterSpacing: "-0.6px",
            textTransform: "uppercase",
            lineHeight: "16px",
          }}
        >
          {label}
        </Text>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: 16,
            color: isPositive ? MOBILE_GAINER : MOBILE_LOSER,
            lineHeight: "24px",
            textTransform: "uppercase",
          }}
        >
          {name || "—"}
        </Text>
      </Stack>
      <Stack spacing="4px" alignItems="flex-end">
        <Text
          sx={{
            fontSize: 12,
            color: theme.mobileLabelColor,
            letterSpacing: "-0.6px",
            textTransform: "uppercase",
            lineHeight: "16px",
            textAlign: "right",
          }}
        >
          IMPACT
        </Text>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: 16,
            color: theme.mobileValueColor,
            lineHeight: "24px",
            textAlign: "right",
          }}
        >
          {impact}
        </Text>
      </Stack>
    </Box>
  );
};

/**
 * Mobile diverging bar chart — 
 * Labels fixed at edges; bars grow from center.
 */
const MobilePerformanceChart = ({ losers, gainers, axisMax, theme }) => {
  const [tooltip, setTooltip] = useState(null);
  const rows = Math.max(losers.length, gainers.length, 1);

  const barWidthPct = (value) => {
    const abs = Math.abs(Number(value) || 0);
    return Math.min(100, (abs / axisMax) * 100);
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "16px",
        border: theme.mobileCardBorder,
        bgcolor: theme.mobileCardBg,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        p: "17px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Text
          sx={{
            fontWeight: 500,
            fontSize: 10,
            color: theme.mobileLabelColor,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            lineHeight: "15px",
          }}
        >
          MARKET PERFORMANCE (24H)
        </Text>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="row" spacing="6px" alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: MOBILE_LOSER,
              }}
            />
            <Text sx={{ fontSize: 10, color: theme.mobileLabelColor, lineHeight: "15px" }}>
              Losers
            </Text>
          </Stack>
          <Stack direction="row" spacing="6px" alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: MOBILE_GAINER,
              }}
            />
            <Text sx={{ fontSize: 10, color: theme.mobileLabelColor, lineHeight: "15px" }}>
              Gainers
            </Text>
          </Stack>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 320,
          pt: 2,
          pb: 1,
        }}
        onClick={() => setTooltip(null)}
      >
        {/* Vertical grid */}
        <Box
          sx={{
            position: "absolute",
            inset: "8px 0 0 0",
            display: "flex",
            justifyContent: "space-between",
            px: 1,
            opacity: 0.1,
            pointerEvents: "none",
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Box
              key={i}
              sx={{
                width: "1px",
                height: "100%",
                bgcolor: theme.textPrimary,
              }}
            />
          ))}
        </Box>
        {/* Center axis */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 8,
            bottom: 0,
            width: "1px",
            bgcolor: theme.dividerColor,
            transform: "translateX(-50%)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        <Stack
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 2, minHeight: 300 }}
        >
          {Array.from({ length: rows }).map((_, i) => {
            const loser = losers[i];
            const gainer = gainers[i];
            const loserW = loser ? barWidthPct(loser.y) : 0;
            const gainerW = gainer ? barWidthPct(gainer.y) : 0;

            return (
              <Box
                key={`row-${i}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 40,
                  width: "100%",
                }}
              >
                {/* Loser half */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    pr: 1,
                    position: "relative",
                    height: 24,
                  }}
                >
                  {loser?.name && (
                    <Text
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontWeight: 700,
                        fontSize: 10,
                        color: theme.mobileLabelColor,
                        lineHeight: "15px",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                    >
                      {loser.name}
                    </Text>
                  )}
                  {loser && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setTooltip({
                          side: "left",
                          row: i,
                          label: loser.name,
                          value: Math.abs(Number(loser.y) || 0),
                        });
                      }}
                      sx={{
                        position: "relative",
                        height: 24,
                        width: `${loserW}%`,
                        bgcolor: MOBILE_LOSER,
                        borderTopLeftRadius: "2px",
                        borderBottomLeftRadius: "2px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {tooltip?.side === "left" && tooltip?.row === i && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: -8,
                            top: -4,
                            px: "7px",
                            py: "3px",
                            borderRadius: "4px",
                            border: "1px solid rgba(255,255,255,0.1)",
                            bgcolor: "rgba(49,59,59,0.9)",
                            backdropFilter: "blur(4px)",
                            whiteSpace: "nowrap",
                            zIndex: 3,
                          }}
                        >
                          <Text
                            sx={{
                              fontSize: 8,
                              color: "#FFFFFF",
                              lineHeight: "12px",
                            }}
                          >
                            {loser.name}{" "}
                            -{Math.abs(Number(loser.y) || 0).toFixed(2)}%
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Gainer half */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    pl: 1,
                    position: "relative",
                    height: 24,
                  }}
                >
                  {gainer && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setTooltip({
                          side: "right",
                          row: i,
                          label: gainer.name,
                          value: Math.abs(Number(gainer.y) || 0),
                        });
                      }}
                      sx={{
                        position: "relative",
                        height: 24,
                        width: `${gainerW}%`,
                        bgcolor: MOBILE_GAINER,
                        borderTopRightRadius: "2px",
                        borderBottomRightRadius: "2px",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                    >
                      {tooltip?.side === "right" && tooltip?.row === i && (
                        <Box
                          sx={{
                            position: "absolute",
                            right: -8,
                            top: -4,
                            px: "7px",
                            py: "3px",
                            borderRadius: "4px",
                            border: "1px solid rgba(255,255,255,0.1)",
                            bgcolor: "rgba(49,59,59,0.9)",
                            backdropFilter: "blur(4px)",
                            whiteSpace: "nowrap",
                            zIndex: 3,
                          }}
                        >
                          <Text
                            sx={{
                              fontSize: 8,
                              color: "#FFFFFF",
                              lineHeight: "12px",
                            }}
                          >
                            {gainer.name}{" "}
                            +{Math.abs(Number(gainer.y) || 0).toFixed(2)}%
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                  {gainer?.name && (
                    <Text
                      sx={{
                        position: "absolute",
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontWeight: 700,
                        fontSize: 10,
                        color: theme.mobileLabelColor,
                        lineHeight: "15px",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                    >
                      {gainer.name}
                    </Text>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      {/* X-axis */}
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: "4px", height: "13.5px" }}
      >
        {["-50%", "-25%", "0", "+25%", "+50%"].map((label) => (
          <Text
            key={label}
            sx={{
              fontSize: 9,
              color: theme.mobileLabelColor,
              lineHeight: "13.5px",
            }}
          >
            {label}
          </Text>
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Top Losers vs Top Gainers — desktop  / mobile 
 * Mobile-only redesign; desktop chart unchanged.
 */
const TopPerformance = ({
  topPerformersGraphData = [],
  topLosersGraphData = [],
  isLoading = false,
}) => {
  const { theme } = usePhcTheme();
  const { losers, performers, axisMax, worst, best } = useMemo(() => {
    const losersRaw = (topLosersGraphData || []).slice(0, MAX_ROWS);
    const performersRaw = (topPerformersGraphData || []).slice(0, MAX_ROWS);

    const losersNorm = losersRaw.map((d) => ({
      ...d,
      name: d.name,
      y: -Math.abs(Number(d.y) || 0),
    }));
    const performersNorm = performersRaw.map((d) => ({
      ...d,
      name: d.name,
      y: Math.abs(Number(d.y) || 0),
    }));

    const maxAbs = Math.max(
      10,
      ...losersNorm.map((d) => Math.abs(d.y || 0)),
      ...performersNorm.map((d) => Math.abs(d.y || 0)),
    );
    const axisMaxVal = Math.max(50, Math.ceil(maxAbs / 10) * 10);

    const worstItem =
      losersRaw.length > 0
        ? [...losersRaw].sort(
            (a, b) => Math.abs(Number(b.y) || 0) - Math.abs(Number(a.y) || 0),
          )[0]
        : null;
    const bestItem =
      performersRaw.length > 0
        ? [...performersRaw].sort(
            (a, b) => Math.abs(Number(b.y) || 0) - Math.abs(Number(a.y) || 0),
          )[0]
        : null;

    return {
      losers: losersNorm,
      performers: performersNorm,
      axisMax: axisMaxVal,
      worst: worstItem,
      best: bestItem,
    };
  }, [topPerformersGraphData, topLosersGraphData]);

  const chartOptions = useMemo(() => {
    const maxLength = Math.max(losers.length, performers.length, 1);

    const loserCategories = Array.from(
      { length: maxLength },
      (_, i) => losers[i]?.name || "",
    );
    const performerCategories = Array.from(
      { length: maxLength },
      (_, i) => performers[i]?.name || "",
    );

    const pad = (data) =>
      Array.from({ length: maxLength }, (_, i) =>
        data?.[i] ? data[i] : { name: "", y: null },
      );

    return {
      chart: {
        type: "bar",
        height: 430,
        backgroundColor: "transparent",
        spacing: [24, 16, 20, 16],
        style: { fontFamily: "inherit" },
      },
      title: { text: null },
      credits: { enabled: false },
      exporting: { enabled: false },
      legend: { enabled: false },
      xAxis: [
        {
          categories: loserCategories,
          reversed: true,
          lineWidth: 0,
          tickLength: 0,
          gridLineWidth: 0,
          labels: {
            align: "right",
            x: -10,
            style: {
              color: theme.chartAxisColor,
              fontSize: "14px",
              fontWeight: "700",
            },
          },
        },
        {
          categories: performerCategories,
          opposite: true,
          reversed: false,
          linkedTo: 0,
          lineWidth: 0,
          tickLength: 0,
          gridLineWidth: 0,
          labels: {
            align: "left",
            x: 10,
            style: {
              color: theme.chartAxisColor,
              fontSize: "14px",
              fontWeight: "700",
            },
          },
        },
      ],
      yAxis: {
        min: -axisMax,
        max: axisMax,
        tickInterval: 10,
        title: { text: null },
        gridLineWidth: 1,
        gridLineColor: theme.chartGridColor,
        lineWidth: 0,
        labels: {
          formatter: function () {
            if (this.value === 0) return "0";
            const abs = Math.abs(this.value);
            return this.value < 0 ? `-${abs}%` : `+${abs}%`;
          },
          style: {
            color: theme.chartAxisColor,
            fontSize: "13px",
            fontWeight: "400",
          },
        },
        plotLines: [
          {
            color: theme.chartPlotLineColor,
            width: 1.25,
            value: 0,
            zIndex: 5,
          },
        ],
      },
      plotOptions: {
        series: {
          stacking: null,
          borderWidth: 0,
          borderRadius: 0,
          pointPadding: 0.18,
          groupPadding: 0.1,
          pointWidth: 24,
          states: {
            hover: { brightness: 0.06 },
            inactive: { opacity: 1 },
          },
          events: {
            legendItemClick: () => false,
          },
        },
        bar: {
          borderRadius: 0,
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: "transparent",
        borderWidth: 0,
        shadow: false,
        padding: 0,
        hideDelay: 60,
        formatter: function () {
          const isLoser = this.series.name === "Top Losers";
          const glow = isLoser
            ? "rgba(255,23,68,0.35)"
            : "rgba(0,230,118,0.35)";
          const value = `${isLoser ? "-" : "+"}${Math.abs(Number(this.point.y) || 0).toFixed(2)}`;
          const label = this.point.name || this.point.category || "";
          const tooltipBg = theme.chartTooltipBg;
          const tooltipText = theme.chartTooltipText;
          const tooltipBorder = theme.chartTooltipBorder;
          const tooltipShadow =
            tooltipBg === "#FFFFFF"
              ? "0 2px 8px rgba(0,0,0,0.12)"
              : `inset 0 0 16px 0 ${glow}`;
          return `
            <div style="
              display:inline-flex;
              align-items:center;
              gap:8px;
              padding:9px;
              border-radius:15px;
              border:0.75px solid ${tooltipBorder};
              background:${tooltipBg};
              box-shadow: ${tooltipShadow};
              color:${tooltipText};
              font-family: inherit;
            ">
              <span style="font-weight:600;font-size:16px;line-height:1;">${label}</span>
              <span style="font-weight:400;font-size:13px;line-height:1;">${value}%</span>
            </div>
          `;
        },
      },
      series: [
        {
          name: "Top Losers",
          data: pad(losers),
          color: LOSER_COLOR,
          xAxis: 0,
        },
        {
          name: "Top Performers",
          data: pad(performers),
          color: GAINER_COLOR,
          xAxis: 1,
        },
      ],
    };
  }, [losers, performers, axisMax, theme]);

  const hasData =
    (topPerformersGraphData?.length || 0) > 0 ||
    (topLosersGraphData?.length || 0) > 0;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: { xs: 0, md: theme.radiusSection },
        border: {
          xs: "none",
          md: `1px solid ${theme.sectionBorder}`,
        },
        backgroundImage: { xs: "none", md: theme.sectionBg },
        backgroundColor: { xs: "transparent", md: theme.cardBg },
        boxShadow: { xs: "none", md: theme.cardShadow },
        p: { xs: 0, md: 2.5 },
        overflow: "hidden",
        "&::after": {
          content: { xs: "none", md: '""' },
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          pointerEvents: "none",
          boxShadow: theme.sectionInsetGlow,
        },
      }}
    >
      {/* —— MOBILE  —— */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Box sx={{ mb: 3 }}>
          <Text
            sx={{
              fontWeight: 600,
              fontSize: 24,
              color: theme.mobileValueColor,
              lineHeight: "32px",
              mb: 0.5,
            }}
          >
            Top Losers vs Top Gainers
          </Text>
          <Text
            sx={{
              fontSize: 14,
              color: theme.mobileLabelColor,
              lineHeight: "20px",
            }}
          >
            Jarvis - Market Momentum Analysis
          </Text>
        </Box>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 320,
            }}
          >
            <CircularProgress sx={{ color: "#1E88E5" }} />
          </Box>
        ) : hasData ? (
          <Stack spacing={2}>
            <MobilePerformanceChart
              losers={losers}
              gainers={performers}
              axisMax={axisMax}
              theme={theme}
            />
            <Stack spacing="16px">
              <MobileInsightCard
                label="WORST PERFORMER"
                name={worst?.name}
                impact={formatPct(-(Math.abs(Number(worst?.y) || 0)))}
                tone="negative"
                theme={theme}
              />
              <MobileInsightCard
                label="BEST PERFORMER"
                name={best?.name}
                impact={formatPct(Math.abs(Number(best?.y) || 0))}
                tone="positive"
                theme={theme}
              />
            </Stack>
          </Stack>
        ) : (
          <Stack
            sx={{
              minHeight: 240,
              alignItems: "center",
              justifyContent: "center",
              color: theme.textMuted,
              fontSize: 14,
            }}
          >
            No data found
          </Stack>
        )}
      </Box>

      {/* —— DESKTOP (unchanged) —— */}
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            position: "relative",
            zIndex: 1,
            fontWeight: 600,
            fontSize: 24,
            color: theme.textPrimary,
            mb: 2.5,
          }}
        >
          Top Losers vs Top Gainers
        </Text>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            borderRadius: theme.radiusCard,
            border: `1px solid ${theme.sectionBorder}`,
            backgroundImage: theme.sectionBg,
            backgroundColor: theme.cardBg,
            boxShadow: theme.cardShadow,
            overflow: "hidden",
            px: 2,
            pt: 2,
            pb: 1.5,
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              pointerEvents: "none",
              boxShadow: theme.insetGlow,
            },
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 360,
              }}
            >
              <CircularProgress sx={{ color: "#1E88E5" }} />
            </Box>
          ) : hasData ? (
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                width: "100%",
              }}
            >
              <Chart options={chartOptions} />
            </Box>
          ) : (
            <Stack
              sx={{
                minHeight: 360,
                alignItems: "center",
                justifyContent: "center",
                color: theme.textMuted,
                fontSize: 16,
              }}
            >
              No data found
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          alignItems="center"
          sx={{ position: "relative", zIndex: 1, mt: 2.5 }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: LOSER_COLOR,
                flexShrink: 0,
                boxShadow: "0 0 10px rgba(255,23,68,0.55)",
              }}
            />
            <Text
              sx={{
                fontWeight: 500,
                fontSize: 16,
                color: theme.textPrimary,
              }}
            >
              Top Losers
            </Text>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                bgcolor: GAINER_COLOR,
                flexShrink: 0,
                boxShadow: "0 0 10px rgba(0,230,118,0.55)",
              }}
            />
            <Text
              sx={{
                fontWeight: 500,
                fontSize: 16,
                color: theme.textPrimary,
              }}
            >
              Top Performers
            </Text>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default TopPerformance;
